import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Inbox,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Filter,
  Plus,
  Minus,
  MessageSquare,
  User,
  Building2,
  Calendar,
  ChevronDown,
  Phone,
  Globe,
  Key,
  Mic,
  Hash,
  ExternalLink
} from 'lucide-react'
import { financialsApi } from '../services/api'
import { useNotifications } from '../context/NotificationContext'
import './RequestInbox.css'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  }
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', icon: Clock, color: '#eab308' },
  { value: 'in_progress', label: 'In Progress', icon: AlertCircle, color: '#3b82f6' },
  { value: 'completed', label: 'Completed', icon: CheckCircle, color: '#22c55e' },
  { value: 'rejected', label: 'Rejected', icon: XCircle, color: '#ef4444' }
]

function RequestInbox() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [requests, setRequests] = useState([])
  const [stats, setStats] = useState(null)
  const [filter, setFilter] = useState('pending')
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [adminNotes, setAdminNotes] = useState('')
  const [processing, setProcessing] = useState(false)
  const { refreshNotifications } = useNotifications()
  const autoRefreshRef = useRef(null)

  // Initial load and filter change
  useEffect(() => {
    loadData()
  }, [filter])

  // Auto-refresh every 30 seconds (silent background refresh)
  useEffect(() => {
    autoRefreshRef.current = setInterval(() => {
      loadData(true, true) // silent refresh
    }, 30000)

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current)
      }
    }
  }, [filter])

  const loadData = async (isRefresh = false, isSilent = false) => {
    // For silent refresh, don't show any loading indicators
    if (!isSilent) {
      if (isRefresh) setRefreshing(true)
      else setLoading(true)
    }

    try {
      const [requestsRes, statsRes] = await Promise.allSettled([
        financialsApi.getRequests(filter === 'all' ? 'all' : filter),
        financialsApi.getRequestStats()
      ])

      if (requestsRes.status === 'fulfilled') {
        setRequests(requestsRes.value.data.requests || [])
      }
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data.stats || null)
      }

      // Refresh the notification badge count
      refreshNotifications()
    } catch (error) {
      console.error('Failed to load requests:', error)
    } finally {
      if (!isSilent) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }

  const handleUpdateStatus = async (requestId, newStatus) => {
    setProcessing(true)
    try {
      await financialsApi.updateRequest(requestId, {
        status: newStatus,
        admin_notes: adminNotes
      })
      setSelectedRequest(null)
      setAdminNotes('')
      loadData(true)
    } catch (error) {
      console.error('Failed to update request:', error)
      alert('Failed to update request')
    } finally {
      setProcessing(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0]
    const Icon = option.icon
    return (
      <span className="status-badge" style={{ background: `${option.color}20`, color: option.color }}>
        <Icon size={12} />
        {option.label}
      </span>
    )
  }

  const getTypeIcon = (type) => {
    if (type === 'bot_addition') return <Plus size={16} className="text-success" />
    if (type === 'bot_removal') return <Minus size={16} className="text-danger" />
    return <MessageSquare size={16} />
  }

  const getTypeLabel = (type) => {
    if (type === 'bot_addition') return 'Bot Addition'
    if (type === 'bot_removal') return 'Bot Removal'
    return type
  }

  if (loading) {
    return (
      <div className="request-inbox-page">
        <div className="loading-state">
          <RefreshCw className="spin" size={32} />
          <p>Loading requests...</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="request-inbox-page"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="page-header" variants={itemVariants}>
        <div className="header-left">
          <h1>Request Inbox</h1>
          <p className="header-subtitle">Manage client bot addition and removal requests</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-ghost"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Stats */}
      {stats && (
        <motion.div className="stat-blocks" variants={itemVariants}>
          <div className="stat-block yellow">
            <div className="stat-icon">
              <Clock size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.pending || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
          </div>
          <div className="stat-block blue">
            <div className="stat-icon">
              <AlertCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.in_progress || 0}</span>
              <span className="stat-label">In Progress</span>
            </div>
          </div>
          <div className="stat-block green">
            <div className="stat-icon">
              <CheckCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
          <div className="stat-block purple">
            <div className="stat-icon">
              <Plus size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.bot_additions || 0}</span>
              <span className="stat-label">Bot Additions</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filter Bar */}
      <motion.div className="filter-bar" variants={itemVariants}>
        <div className="filter-dropdown">
          <button
            className="filter-btn"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <Filter size={16} />
            {STATUS_OPTIONS.find(o => o.value === filter)?.label || 'All'}
            <ChevronDown size={14} />
          </button>
          {showFilterDropdown && (
            <>
              <div className="dropdown-backdrop" onClick={() => setShowFilterDropdown(false)} />
              <div className="filter-menu">
                <button
                  className={filter === 'all' ? 'active' : ''}
                  onClick={() => { setFilter('all'); setShowFilterDropdown(false) }}
                >
                  All Requests
                </button>
                {STATUS_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    className={filter === option.value ? 'active' : ''}
                    onClick={() => { setFilter(option.value); setShowFilterDropdown(false) }}
                  >
                    <option.icon size={14} style={{ color: option.color }} />
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <span className="filter-count">{requests.length} request{requests.length !== 1 ? 's' : ''}</span>
      </motion.div>

      {/* Request List */}
      <motion.div className="requests-list" variants={itemVariants}>
        {requests.length === 0 ? (
          <div className="empty-state">
            <Inbox size={48} />
            <p>No {filter !== 'all' ? filter : ''} requests found</p>
          </div>
        ) : (
          requests.map(request => (
            <motion.div
              key={request.id}
              className={`request-card ${selectedRequest?.id === request.id ? 'selected' : ''}`}
              onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <div className="request-card-header">
                <div className="request-type">
                  {getTypeIcon(request.type)}
                  <span>{getTypeLabel(request.type)}</span>
                </div>
                {getStatusBadge(request.status)}
              </div>

              <div className="request-card-body">
                {/* Client Info */}
                <div className="request-client">
                  <Building2 size={14} />
                  <span className="client-name">{request.client_name}</span>
                  {request.client_company && (
                    <span className="client-company">({request.client_company})</span>
                  )}
                </div>

                {/* Request Details Grid */}
                <div className="request-details-grid">
                  {/* Bot Count - check bot_count, bot_ids, or calculate from sips range */}
                  {(() => {
                    let count = request.bot_count
                    // If no bot_count but bot_ids exists, parse and count
                    if (!count && request.bot_ids) {
                      try {
                        const ids = typeof request.bot_ids === 'string'
                          ? JSON.parse(request.bot_ids)
                          : request.bot_ids
                        count = Array.isArray(ids) ? ids.length : 0
                      } catch (e) {
                        count = 0
                      }
                    }
                    // If still no count, check sips field for range (e.g., "1001-1010")
                    if (!count && request.sips) {
                      const rangeMatch = request.sips.match(/^(\d+)-(\d+)$/)
                      if (rangeMatch) {
                        const start = parseInt(rangeMatch[1], 10)
                        const end = parseInt(rangeMatch[2], 10)
                        count = end - start + 1
                      }
                    }
                    return count > 0 ? (
                      <div className="detail-item">
                        <Hash size={12} />
                        <span><strong>{count}</strong> bot{count !== 1 ? 's' : ''}</span>
                      </div>
                    ) : null
                  })()}

                  {/* Campaign */}
                  {request.campaign && (
                    <div className="detail-item">
                      <Mic size={12} />
                      <span>{request.campaign}</span>
                    </div>
                  )}

                  {/* Dialer */}
                  {(request.dialer_name || request.is_new_dialer) && (
                    <div className="detail-item">
                      <Phone size={12} />
                      <span>{request.is_new_dialer ? <em>New Dialer</em> : request.dialer_name}</span>
                    </div>
                  )}

                  {/* Priority */}
                  {request.priority && request.priority !== 'normal' && (
                    <div className={`detail-item priority-${request.priority}`}>
                      <AlertCircle size={12} />
                      <span>{request.priority.toUpperCase()}</span>
                    </div>
                  )}

                  {/* SIP Range */}
                  {request.sips && (
                    <div className="detail-item">
                      <Key size={12} />
                      <span>SIP: {request.sips}</span>
                    </div>
                  )}

                  {/* Dialer URL */}
                  {request.dialer_url && (
                    <div className="detail-item">
                      <Globe size={12} />
                      <a href={request.dialer_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                        {new URL(request.dialer_url).hostname}
                        <ExternalLink size={10} />
                      </a>
                    </div>
                  )}
                </div>

                {/* Comments */}
                {request.comments && (
                  <div className="request-notes">
                    <MessageSquare size={12} />
                    <span>{request.comments}</span>
                  </div>
                )}

                {/* Meta info */}
                <div className="request-meta">
                  <span><Calendar size={12} /> {formatDate(request.created_at)}</span>
                  {request.processed_by_name && (
                    <span><User size={12} /> {request.processed_by_name}</span>
                  )}
                </div>
              </div>

              {/* Expanded Actions */}
              <AnimatePresence mode="wait">
                {selectedRequest?.id === request.id && (
                  <motion.div
                    className="request-actions"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="admin-notes-input">
                      <label>Admin Notes</label>
                      <textarea
                        value={adminNotes}
                        onChange={e => setAdminNotes(e.target.value)}
                        placeholder="Add notes about this request..."
                        rows={2}
                      />
                    </div>

                    {request.admin_notes && (
                      <div className="previous-notes">
                        <strong>Previous notes:</strong> {request.admin_notes}
                      </div>
                    )}

                    <div className="action-buttons">
                      {request.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-warning"
                            onClick={() => handleUpdateStatus(request.id, 'in_progress')}
                            disabled={processing}
                          >
                            <AlertCircle size={14} />
                            Start Processing
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleUpdateStatus(request.id, 'rejected')}
                            disabled={processing}
                          >
                            <XCircle size={14} />
                            Reject
                          </button>
                        </>
                      )}
                      {request.status === 'in_progress' && (
                        <>
                          <button
                            className="btn btn-success"
                            onClick={() => handleUpdateStatus(request.id, 'completed')}
                            disabled={processing}
                          >
                            <CheckCircle size={14} />
                            Mark Complete
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleUpdateStatus(request.id, 'rejected')}
                            disabled={processing}
                          >
                            <XCircle size={14} />
                            Reject
                          </button>
                        </>
                      )}
                      {(request.status === 'completed' || request.status === 'rejected') && (
                        <button
                          className="btn btn-ghost"
                          onClick={() => handleUpdateStatus(request.id, 'pending')}
                          disabled={processing}
                        >
                          <Clock size={14} />
                          Reopen
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))
        )}
      </motion.div>
    </motion.div>
  )
}

export default RequestInbox
